import { useEffect } from 'react'

import { useCreation, useRequest } from 'ahooks'
import { isNil } from 'lodash'

import { useUrlParams } from '@eastcoal/common/hooks/useUrlParams'
import { GenericalRecord, KeySet } from '@eastcoal/common/types/utilities'
import { pick } from '@eastcoal/common/utils/object'
import { promiseNoop } from '@eastcoal/common/utils/promise'

import { FormMode } from '../../enums/FormMode'
import { useCrudFormStore, useCrudFormStoreApi } from '../hooks/useStore'
import { CrudFormAction, CrudFormState } from '../store/interface'

import { CrudFormStoreUpdaterProps } from './interface'

const useStoreUpdater = <
  K extends KeySet<
    CrudFormState<GenericalRecord, GenericalRecord, GenericalRecord>
  >
>(
  key: K,
  value: CrudFormState<GenericalRecord, GenericalRecord, GenericalRecord>[K]
) => {
  const { setState } = useCrudFormStoreApi()

  useEffect(() => {
    setState({ [key]: value })
  }, [key, value, setState])
}

const useStoreSetter = <
  K extends KeySet<CrudFormAction<GenericalRecord, GenericalRecord>>
>(
  setter: K,
  value: any,
  condition?: boolean
) => {
  const { getState } = useCrudFormStoreApi()

  useEffect(() => {
    if (condition !== false) {
      getState()[setter](value)
    }
  }, [value, setter, condition, getState])
}

export const CrudFormStoreUpdater = <
  FormData extends GenericalRecord,
  DetailData extends GenericalRecord,
  Context
>({
  context,
  readApi: readApiFactory,
  createApi,
  updateApi,
  /* -------------------------------------------------------------------------- */
  /*                                CrudFormStore                               */
  /* -------------------------------------------------------------------------- */
  mode: extrenalMode,
  onModeChange,
  onSuccess
}: CrudFormStoreUpdaterProps<FormData, DetailData, Context>) => {
  const { mode } = useCrudFormStore(state => pick(state, ['mode']))

  const urlParams = useUrlParams()
  const mergedContext: any = useCreation(
    () => ({
      ...urlParams,
      ...context,
      formMode: mode
    }),
    [context, urlParams, mode]
  )

  const {
    api: readApi,
    ready,
    refreshDeps
  } = readApiFactory?.(mergedContext) ?? {}

  const {
    data: readData = {} as DetailData,
    error: readError,
    loading: readLoading,
    refreshAsync
  } = useRequest(readApi ?? promiseNoop, {
    manual: false,
    ready:
      (mode === FormMode.READ || mode === FormMode.UPDATE) &&
      readApi &&
      ready !== false,
    refreshDeps
  })

  const submitApi =
    (mode === FormMode.CREATE ? createApi : updateApi) ?? promiseNoop

  const {
    error: submitError,
    loading: submitLoading,
    runAsync: submitRunAsync
  } = useRequest(
    (values, submitContext) =>
      submitApi(values, { ...mergedContext, ...submitContext }),
    {
      manual: true
    }
  )

  useStoreSetter('setDeatilData', readData)
  useStoreUpdater('readError', readError)
  useStoreUpdater('readLoading', readLoading)
  useStoreUpdater('reloadDetail', refreshAsync)

  useStoreUpdater('submitError', submitError)
  useStoreUpdater('submitLoading', submitLoading)
  useStoreUpdater('submitRequest', submitRunAsync)

  useStoreUpdater('context', mergedContext)
  useStoreUpdater('onSuccess', onSuccess)

  useStoreSetter('setMode', extrenalMode, !isNil(extrenalMode))
  useStoreUpdater('onModeChange', onModeChange)

  return null
}
